import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { Game, GamesQueryParams } from '../types/gameTypes';
import mockGamesData from './mockMyGames.json';

const cloneMockGames = (): Game[] => JSON.parse(JSON.stringify(mockGamesData)) as Game[];

let MOCK_GAMES: Game[] = cloneMockGames();

/** Test-only: restores the in-memory mock games array to its initial snapshot. */
export const resetMockGamesForTests = (): void => {
  MOCK_GAMES = cloneMockGames();
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getGamesProvidesTags = (result: Game[] | undefined) =>
  result
    ? [
        ...result.map(({ id }) => ({ type: 'Game' as const, id })),
        { type: 'Game' as const, id: 'LIST' },
      ]
    : [{ type: 'Game' as const, id: 'LIST' }];

export const gamesApi = createApi({
  reducerPath: 'gamesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Game'],
  endpoints: (builder) => ({
    getGames: builder.query<Game[], GamesQueryParams | void>({
      queryFn: async (params) => {
        await delay(150);
        let filtered = [...MOCK_GAMES];
        const tag = params?.tag;

        if (tag) {
          filtered = filtered.filter((g) => g.tags.includes(tag));
        }

        return { data: filtered };
      },
      providesTags: getGamesProvidesTags,
    }),
    getGameById: builder.query<Game | null, string>({
      queryFn: async (id) => {
        await delay(100);
        const game = MOCK_GAMES.find((g) => g.id === id || g.slug === id) || null;
        return { data: game };
      },
      providesTags: (result, error, id) => [{ type: 'Game' as const, id }],
    }),
    addGame: builder.mutation<Game, Game>({
      queryFn: async (newGameData) => {
        await delay(150);
        const newGame: Game = {
          ...newGameData,
          id: newGameData.id || `game-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        };
        MOCK_GAMES.unshift(newGame);
        return { data: newGame };
      },
      invalidatesTags: [{ type: 'Game' as const, id: 'LIST' }],
    }),
    updateGame: builder.mutation<Game, Game>({
      queryFn: async (updatedGame) => {
        await delay(150);
        const index = MOCK_GAMES.findIndex((g) => g.id === updatedGame.id);
        if (index !== -1) {
          MOCK_GAMES[index] = { ...MOCK_GAMES[index], ...updatedGame };
        } else {
          MOCK_GAMES.unshift({ ...updatedGame });
        }
        return { data: updatedGame };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Game' as const, id: arg.id },
        { type: 'Game' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const { useGetGamesQuery, useGetGameByIdQuery, useAddGameMutation, useUpdateGameMutation } =
  gamesApi;
